import {Component, OnInit} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {FormlyFieldConfig, FormlyFormOptions} from '@ngx-formly/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {PartnerService} from '../services/partner.service';
import {ProfileService} from '../../../../auth/services/profile.service';
import {UpdatePartnerRequest} from '../../../../models/requests/update-partner-request.model';
import {AlertNotificationsService} from '../../../../../services/alert-notifications.service';
import {TranslateService} from '@ngx-translate/core';
import {linkValidator} from '../../../../web/components/validators';
import {buildValidationMessages} from '../../../../web/components/error-message.service';
import {atLeastOneSocialValidator, requireAtLeastOneSocial} from '../../../../shared/formly-config.module';
import {NgxSpinnerService} from 'ngx-spinner';

@Component({
  selector: 'app-partner-account-update',
  templateUrl: './partner-account-update.component.html',
  styleUrls: ['./partner-account-update.component.css'],
  standalone: false
})
export class PartnerAccountUpdateComponent implements OnInit {
  updateFields: FormlyFieldConfig[] = [];
  coverFields: FormlyFieldConfig[] = [];
  socialFields: FormlyFieldConfig[] = [];
  languageFields: FormlyFieldConfig[] = [];
  updateForm = new FormGroup({});
  updateModel = {} as UpdatePartnerRequest;
  isSubmitting = false;
  activeTab = 'cover';
  currentCoverUrl: string | null = null;
  newCoverPreview: string | null = null;
  newCoverFile: File | null = null;
  uploadError: string | null = null;
  isDragOver = false;
  updateOptions: FormlyFormOptions = {
    formState: {
      validationMessages: buildValidationMessages()
    }
  };

  private readonly socialKeys = ['website', 'fbLink', 'igLink', 'liLink', 'ytLink', 'twitterLink'];

  get socialsInvalid(): boolean {
    return !!this.updateForm.errors?.['atLeastOneSocialLink']
      || this.socialKeys.some(key => this.updateForm.get(key)?.invalid);
  }

  get languagesInvalid(): boolean {
    return !!this.updateForm.get('languagesSpoken')?.invalid;
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  constructor(
    private partnerService: PartnerService,
    protected profileService: ProfileService,
    private notificationAlert: AlertNotificationsService,
    private translateService: TranslateService,
    private spinner: NgxSpinnerService,
    public activeModal: NgbActiveModal
  ) {
  }

  ngOnInit() {
    this.initializeForm();
    this.loadCurrentData();
  }

  initializeForm() {
    this.languageFields = [
      {
        key: 'languagesSpoken',
        type: 'multi-language-select',
        props: {
          label: this.translateService.instant('Languages Spoken'),
          showLabel: true,
          required: true,
          options: [],
          showIcons: true,
          getIconSrc: (code: any) => `https://flagcdn.com/w320/${code}.png`
        },
        validators: {
          required: {
            expression: (control: any) => control.value && control.value.length > 0,
            message: this.translateService.instant('At least one language must be selected')
          }
        }
      },
    ];

    this.coverFields = [];

    this.socialFields = [
      {
        templateOptions: {
          label: 'Social Media Links',
        },
        fieldGroup: [
          {
            key: 'website',
            type: 'input',
            templateOptions: {
              label: 'Website',
              type: 'url',
              placeholder: 'https://example.com',
              required: true,
            },
            validators: {
              link: {
                expression: linkValidator,
              },
            },
            expressionProperties: {
              'templateOptions.required': requireAtLeastOneSocial,
            },
          },
          {
            key: 'fbLink',
            type: 'input',
            templateOptions: {
              label: 'Facebook',
              type: 'url',
              placeholder: 'https://facebook.com/yourpage',
            },
            validators: {
              link: {
                expression: linkValidator,
              },
            },
            expressionProperties: {
              'templateOptions.required': requireAtLeastOneSocial,
            },
          },
          {
            key: 'igLink',
            type: 'input',
            templateOptions: {
              label: 'Instagram',
              type: 'url',
              placeholder: 'https://instagram.com/yourpage',
            },
            validators: {
              link: {
                expression: linkValidator,
              },
            },
            expressionProperties: {
              'templateOptions.required': requireAtLeastOneSocial,
            },
          },
          {
            key: 'liLink',
            type: 'input',
            templateOptions: {
              label: 'LinkedIn',
              type: 'url',
              placeholder: 'https://linkedin.com/company/yourcompany',
            },
            validators: {
              link: {
                expression: linkValidator,
              },
            },
            expressionProperties: {
              'templateOptions.required': requireAtLeastOneSocial,
            },
          },
          {
            key: 'ytLink',
            type: 'input',
            templateOptions: {
              label: 'YouTube',
              type: 'url',
              placeholder: 'https://youtube.com/channel/yourchannel',
            },
            validators: {
              link: {
                expression: linkValidator,
              },
            },
            expressionProperties: {
              'templateOptions.required': requireAtLeastOneSocial,
            },
          },
          {
            key: 'twitterLink',
            type: 'input',
            templateOptions: {
              label: 'X (Twitter)',
              type: 'url',
              placeholder: 'https://x.com/yourhandle',

            },
            validators: {
              link: {
                expression: linkValidator,
              },
            },
            expressionProperties: {
              'templateOptions.required': requireAtLeastOneSocial,
            },
          },
        ],
        validators: {
          atLeastOneSocialLink: {
            expression: atLeastOneSocialValidator,
            message: 'At least one social media link or website is required',
          },
        },
      },
    ];
  }

  loadCurrentData() {
    const partner = this.profileService.profile?.partner;
    if (!partner) return;

    this.currentCoverUrl = partner.coverUrl || null;

    this.updateModel = {
      languagesSpoken: partner.languagesSpoken || [],
      website: partner.website || '',
      twitterLink: partner.twitterLink || '',
      fbLink: partner.fbLink || '',
      igLink: partner.igLink || '',
      liLink: partner.liLink || '',
      ytLink: partner.ytLink || '',
    };

    this.updateForm.patchValue(this.updateModel, {emitEvent: false});
  }

  async submit() {
    if (this.updateForm.valid) {
      this.spinner.show();
      this.isSubmitting = true;
      try {
        const updateRequest: UpdatePartnerRequest = {
          ...this.updateForm.value,
        };

        if (this.newCoverFile) {
          updateRequest.cover = this.newCoverFile;
        }
        updateRequest.languagesSpoken = this.updateForm.get('languagesSpoken')?.value ?? [];
        const repsonse = await this.partnerService.updatePartner(updateRequest);
        this.notificationAlert.success('Partner updated successfully');
        this.activeModal.close('saved');

      } catch (error) {
        console.error('Error updating partner:', error);
      } finally {
        this.spinner.hide();
        this.isSubmitting = false;
        await this.profileService.getProfile();
        if (this.profileService.profile?.partner?.status != 'APPROVED') {
          window.location.reload();
        }
      }
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      this.validateAndPreviewFile(file);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.validateAndPreviewFile(files[0]);
    }
  }

  validateAndPreviewFile(file: File) {
    this.uploadError = null;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      this.uploadError = 'Only PNG, JPG, and JPEG files are allowed';
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError = 'File size must be less than 5MB';
      return;
    }

    // Create preview
    this.newCoverFile = file;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.newCoverPreview = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  removeCover() {
    this.currentCoverUrl = null;
    this.newCoverFile = null;
    this.newCoverPreview = null;
  }

  cancelNewCover() {
    this.newCoverFile = null;
    this.newCoverPreview = null;
    this.uploadError = null;
  }
}
